import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { MainLayout } from "@/layouts/MainLayout";
import { agentApi } from "@/api";
import type { AgentDetails } from "@/types/agent.types";
import { getExternalURL } from "@/config";
import html2canvas from "html2canvas";
import qrcode from "qrcode-generator";
import styles from "./Resources.module.css";

/* ============================================================
   SVG path for the ClickPe "cp" logo icon (reused in cards)
   ============================================================ */
const CP_LOGO_PATH =
  "M21.865 21.8818L21.8589 21.8757L23.9996 19.7349L23.9999 19.7352L26.5723 17.1629C29.1785 14.5567 33.4039 14.5567 36.0101 17.1629C38.6162 19.7691 38.6162 23.9945 36.0101 26.6007C33.4039 29.2068 29.1785 29.2068 26.5723 26.6007L25.0319 25.0603V37.2694L22.0045 37.2694V26.0238L21.4276 26.6007C18.8214 29.2068 14.596 29.2068 11.9898 26.6007C9.38361 23.9945 9.38361 19.7691 11.9898 17.1629C14.596 14.5567 18.8214 14.5567 21.4276 17.1629L21.8589 17.5942L19.7182 19.735L19.2869 19.3036C17.863 17.8797 15.5544 17.8797 14.1305 19.3036C12.7066 20.7275 12.7066 23.0361 14.1305 24.46C15.5544 25.8838 17.863 25.8838 19.2869 24.46L21.865 21.8818ZM26.1348 21.8818L28.713 24.46C30.1369 25.8838 32.4455 25.8838 33.8694 24.46C35.2932 23.0361 35.2932 20.7275 33.8694 19.3036C32.4455 17.8797 30.1369 17.8797 28.713 19.3036L26.1348 21.8818Z";

/* ============================================================
   Inline SVG Components used in cards
   ============================================================ */
const ClickPeLogoWhiteBg: React.FC = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="24" cy="24" r="24" fill="white" />
    <path fillRule="evenodd" clipRule="evenodd" d={CP_LOGO_PATH} fill="#295BEE" />
  </svg>
);

const ClickPeLogoBlueBg: React.FC = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="24" cy="24" r="24" fill="#295BEE" />
    <path fillRule="evenodd" clipRule="evenodd" d={CP_LOGO_PATH} fill="white" />
  </svg>
);

const PhoneIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const EmailIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect width="20" height="16" x="2" y="4" rx="2" />
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
  </svg>
);

const DownloadIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const UsersSmallIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

/* ============================================================
   QR Code Generator Helper
   ============================================================ */
function generateQRDataURL(agent: AgentDetails): string {
  const qr = qrcode(0, "M");
  const productId = "";
  const compositeKey = `${agent.agent_id}:${productId}`;
  const encodedRef = btoa(compositeKey);
  const baseUrl = getExternalURL("CUSTOMER_PORTAL");
  const loginLink = `${baseUrl}/login?ref=${encodedRef}`;
  qr.addData(loginLink);
  qr.make();
  const size = 3;
  const margin = 0;
  const cellSize = size;
  const count = qr.getModuleCount();
  const totalSize = count * cellSize + margin * 2;
  const canvas = document.createElement("canvas");
  canvas.width = totalSize;
  canvas.height = totalSize;
  const ctx = canvas.getContext("2d");
  if (ctx) {
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, totalSize, totalSize);
    ctx.fillStyle = "#000000";
    for (let row = 0; row < count; row++) {
      for (let col = 0; col < count; col++) {
        if (qr.isDark(row, col)) {
          ctx.fillRect(
            col * cellSize + margin,
            row * cellSize + margin,
            cellSize,
            cellSize
          );
        }
      }
    }
  }
  return canvas.toDataURL("image/png");
}

/* ============================================================
   Export Helper
   ============================================================ */
function exportCardAsPNG(
  elementRef: React.RefObject<HTMLDivElement | null>,
  filename: string
) {
  if (!elementRef.current) return;
  html2canvas(elementRef.current, {
    scale: 3,
    useCORS: true,
    backgroundColor: "#ffffff",
    logging: false,
  }).then((canvas) => {
    const link = document.createElement("a");
    link.download = `${filename}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  });
}

function getAgentDisplayInfo(agent: AgentDetails) {
  const fname = agent.fname || "";
  const mname = agent.mname || "";
  const lname = agent.lname || "";

  // Full name includes mname if present
  const fullName = [fname, mname, lname].filter(Boolean).join(" ") || "Agent";

  // Initials: first letter of fname + first letter of lname (skip mname)
  const firstInitial = fname[0] || "";
  const lastInitial = lname[0] || "";
  const initials = (firstInitial + lastInitial).toUpperCase() || "?";

  const phone = `+91 ${agent.mob_num}`;

  return { fullName, initials, phone };
}

/* ============================================================
   ID Card Component
   ============================================================ */
const IdCard: React.FC<{ agent: AgentDetails; qrDataURL: string }> = ({
  agent,
  qrDataURL,
}) => {
  const { fullName, initials, phone } = getAgentDisplayInfo(agent);

  return (
    <>
      {/* Header */}
      <div className={styles.idCardHeader}>
        <div className={styles.idCardHeaderBlue}>
          <div className={styles.idCardHeaderBlueAngle} />
        </div>
        <div className={styles.idCardHeaderGreen} />
        <div className={styles.idCardHeaderContent}>
          <div className={styles.idCardLogo}>
            <ClickPeLogoWhiteBg />
            <span className={styles.idCardLogoText}>ClickPe</span>
          </div>
          <span className={styles.idCardBadge}>ID Card</span>
        </div>
      </div>

      {/* Avatar */}
      <div className={styles.idCardAvatarWrapper}>
        <div className={styles.idCardAvatar}>
          {initials}
          <div className={styles.idCardAvatarDot} />
        </div>
      </div>

      {/* Body */}
      <div className={styles.idCardBody}>
        <div className={styles.idCardName}>{fullName}</div>
        <div className={styles.idCardDesignation}>ClickPe DSA Partner</div>
        <div className={styles.idCardPartnerId}>
          <UsersSmallIcon />
          <span>Partner ID: {agent.agent_id}</span>
        </div>

        {/* Contact */}
        <div className={styles.idCardContact}>
          <div className={styles.idCardContactRow}>
            <div className={styles.idCardContactIcon}>
              <PhoneIcon />
            </div>
            <span>{phone}</span>
          </div>
          <div className={styles.idCardContactRow}>
            <div className={styles.idCardContactIcon}>
              <EmailIcon />
            </div>
            <span>{agent.email}</span>
          </div>
        </div>

        {/* QR Code */}
        <div className={styles.idCardQrSection}>
          <div className={styles.idCardQr}>
            <img src={qrDataURL} alt="QR Code" />
          </div>
          <span className={styles.idCardScanText}>Scan to connect</span>
        </div>
      </div>

      {/* Bottom accent */}
      <div className={styles.idCardBottomAccent}>
        <div className={styles.idCardBottomAccentGreen} />
        <div className={styles.idCardBottomAccentBlue} />
      </div>
    </>
  );
};

/* ============================================================
   Visiting Card Component
   ============================================================ */
const VisitingCard: React.FC<{ agent: AgentDetails }> = ({ agent }) => {
  const { fullName, phone } = getAgentDisplayInfo(agent);

  return (
    <>
      {/* Background SVG */}
      <svg
        className={styles.vcBgSvg}
        viewBox="0 0 600 340"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="none"
      >
        <polygon points="0,0 170,0 170,340 0,340" fill="#295BEE" />
        <polygon points="0,0 100,0 100,340 0,340" fill="#001143" opacity="0.2" />
        <line x1="170" y1="0" x2="170" y2="340" stroke="#1CFF7D" strokeWidth="2" opacity="0.5" />
        <g transform="translate(-15, 60) scale(4.5)" opacity="0.1">
          <path fillRule="evenodd" clipRule="evenodd" d={CP_LOGO_PATH} fill="white" />
        </g>
      </svg>

      {/* Content */}
      <div className={styles.vcContent}>
        <div className={styles.vcTop}>
          <div className={styles.vcNameSection}>
            <div className={styles.vcName}>{fullName}</div>
            <div className={styles.vcDesignation}>ClickPe DSA Partner</div>
          </div>
          <div className={styles.vcLogo}>
            <ClickPeLogoBlueBg />
            <span className={styles.vcLogoText}>ClickPe</span>
          </div>
        </div>

        <div className={styles.vcContact}>
          <div className={styles.vcContactRow}>
            <div className={styles.vcContactIcon}>
              <PhoneIcon />
            </div>
            <span>{phone}</span>
          </div>
          <div className={styles.vcContactRow}>
            <div className={styles.vcContactIcon}>
              <EmailIcon />
            </div>
            <span>{agent.email}</span>
          </div>
        </div>

        <div className={styles.vcServices}>
          <span className={styles.vcServiceItem}>Credit Card</span>
          <span className={styles.vcServiceDot} />
          <span className={styles.vcServiceItem}>Personal Loan</span>
          <span className={styles.vcServiceDot} />
          <span className={styles.vcServiceItem}>Business Loan</span>
          <span className={styles.vcServiceDot} />
          <span className={styles.vcServiceItem}>Gold Loan</span>
          <span className={styles.vcServiceDot} />
          <span className={styles.vcServiceItem}>Savings A/C</span>
        </div>
      </div>

      {/* Bottom accent */}
      <div className={styles.vcBottomLine}>
        <div className={styles.vcBottomLineGreen} />
        <div className={styles.vcBottomLineBlue} />
      </div>
    </>
  );
};

/* ============================================================
   Resources Page
   ============================================================ */
export const Resources: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();

  const [agentData, setAgentData] = useState<AgentDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [qrDataURL, setQrDataURL] = useState("");

  const idCardRef = useRef<HTMLDivElement>(null);
  const visitingCardRef = useRef<HTMLDivElement>(null);

  const agentIdOverride = searchParams.get("agentId");

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/agent-login");
    }
  }, [isAuthenticated, navigate]);

  const fetchAgentDetails = useCallback(async () => {
    setIsLoading(true);
    setError("");

    try {
      const agentId =
        agentIdOverride ||
        localStorage.getItem("agent_id") ||
        localStorage.getItem("agentId");

      if (!agentId) {
        setError("Agent ID not found");
        setIsLoading(false);
        return;
      }

      const response = await agentApi.getAgentDetails({
        query_type: "get_agent_details",
        agent_id: agentId,
      });

      const isSuccess =
        (response.status === "Success" || response.message === "Success") &&
        response.response &&
        Array.isArray(response.response) &&
        response.response.length > 0;

      if (isSuccess) {
        const data = response.response[0];
        setAgentData(data);
        // Generate QR code
        setQrDataURL(generateQRDataURL(data));
      } else {
        setError(response.message || "Failed to fetch agent details");
      }
    } catch (err) {
      setError("Failed to fetch agent details. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [agentIdOverride]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchAgentDetails();
    }
  }, [isAuthenticated, fetchAgentDetails]);

  if (!isAuthenticated) {
    return null;
  }

  return (
    <MainLayout>
      <div className={styles.pageContainer}>
        <div className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>Resources</h1>
          <p className={styles.pageSubtitle}>
            Your partner cards and documents
          </p>
        </div>

        {isLoading && (
          <div className={styles.loadingContainer}>Loading...</div>
        )}

        {error && <div className={styles.errorContainer}>{error}</div>}

        {!isLoading && !error && agentData && (
          <div className={styles.cardsContainer}>
            {/* ID Card */}
            <div className={styles.cardSection}>
              <span className={styles.cardLabel}>ID Card</span>
              <div className={styles.idCard} ref={idCardRef}>
                <IdCard agent={agentData} qrDataURL={qrDataURL} />
              </div>
              <button
                className={styles.exportBtn}
                onClick={() => exportCardAsPNG(idCardRef, "ClickPe_ID_Card")}
              >
                <DownloadIcon />
                Download PNG
              </button>
            </div>

            {/* Visiting Card */}
            <div className={styles.cardSection}>
              <span className={styles.cardLabel}>Visiting Card</span>
              <div className={styles.visitingCard} ref={visitingCardRef}>
                <VisitingCard agent={agentData} />
              </div>
              <button
                className={styles.exportBtn}
                onClick={() =>
                  exportCardAsPNG(visitingCardRef, "ClickPe_Visiting_Card")
                }
              >
                <DownloadIcon />
                Download PNG
              </button>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};
